import { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Plus, Trash, GearSix } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatApiError } from "../../lib/utils-extra";

export function GeneralSettings() {
  const [s, setS] = useState(null);
  const reload = () => api.get("/settings").then(({ data }) => setS(data));
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      await api.put("/settings", s);
      toast.success("Settings saved");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!s) return <div className="text-sm text-[#475569]">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="general-section">
      <div className="font-display text-xl font-semibold flex items-center gap-2">
        <GearSix size={20} weight="duotone" color="#0EA5E9" /> General
      </div>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-6 space-y-4">
          <div className="font-display text-lg font-semibold">Company</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="small-label">Company Name</Label>
              <Input className="mt-2" value={s.company_name || ""} onChange={(e) => setS({ ...s, company_name: e.target.value })} data-testid="settings-company-name" />
            </div>
            <div>
              <Label className="small-label">Default Currency</Label>
              <Input className="mt-2" value={s.currency || ""} onChange={(e) => setS({ ...s, currency: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="small-label">PDF Footer</Label>
            <Input className="mt-2" value={s.pdf_footer || ""} onChange={(e) => setS({ ...s, pdf_footer: e.target.value })} />
          </div>
        </CardContent>
      </Card>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-6 space-y-4">
          <div className="font-display text-lg font-semibold">Lists</div>
          <ListEditor label="KPI Categories" items={s.kpi_categories || []} onChange={(v) => setS({ ...s, kpi_categories: v })} testid="kpi-categories-list" />
          <ListEditor label="Allowed Currencies" items={s.currencies || []} onChange={(v) => setS({ ...s, currencies: v })} testid="currencies-list" />
        </CardContent>
      </Card>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-6 space-y-4">
          <div className="font-display text-lg font-semibold">Performance Interpretation</div>
          <p className="text-xs text-[#475569]">Score bands used to label final evaluation scores.</p>
          <div className="space-y-2">
            {(s.interpretation_bands || []).map((b, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-4" value={b.label} onChange={(e) => {
                  const v = [...s.interpretation_bands]; v[i] = { ...v[i], label: e.target.value }; setS({ ...s, interpretation_bands: v });
                }} />
                <Input type="number" className="col-span-3" placeholder="Min" value={b.min_score} onChange={(e) => {
                  const v = [...s.interpretation_bands]; v[i] = { ...v[i], min_score: Number(e.target.value) }; setS({ ...s, interpretation_bands: v });
                }} />
                <Input type="number" className="col-span-3" placeholder="Max" value={b.max_score} onChange={(e) => {
                  const v = [...s.interpretation_bands]; v[i] = { ...v[i], max_score: Number(e.target.value) }; setS({ ...s, interpretation_bands: v });
                }} />
                <input type="color" className="col-span-1 h-10 w-full rounded-lg border border-[#E2E8F0]" value={b.color || "#0EA5E9"} onChange={(e) => {
                  const v = [...s.interpretation_bands]; v[i] = { ...v[i], color: e.target.value }; setS({ ...s, interpretation_bands: v });
                }} />
                <Button variant="ghost" size="sm" className="col-span-1" onClick={() => {
                  const v = s.interpretation_bands.filter((_, idx) => idx !== i); setS({ ...s, interpretation_bands: v });
                }}><Trash size={14} className="text-[#DC2626]" /></Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setS({
            ...s,
            interpretation_bands: [...(s.interpretation_bands || []), { label: "", min_score: 0, max_score: 0, color: "#0EA5E9" }]
          })}>
            <Plus size={14} className="mr-1" /> Add band
          </Button>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="save-settings-button">Save settings</Button>
      </div>
    </div>
  );
}

function ListEditor({ label, items, onChange, testid }) {
  const [val, setVal] = useState("");
  const add = () => {
    if (!val.trim()) return;
    onChange([...items, val.trim()]); setVal("");
  };
  return (
    <div>
      <Label className="small-label">{label}</Label>
      <div className="mt-2 flex gap-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
               data-testid={testid} />
        <Button variant="outline" onClick={add}><Plus size={14} /></Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {items.map((it, i) => (
          <Badge key={i} className="bg-[#F1F5F9] text-[#0F172A] border-0 pl-3 pr-1 py-1.5">
            {it}
            <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="ml-1.5 p-0.5 rounded-full hover:bg-white/60">
              <Trash size={12} />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

export default GeneralSettings;
